// organisms/Carousel.tsx
"use client"

import * as React from "react"
import useEmblaCarousel from "embla-carousel-react"
import { CarouselContext } from "@/contexts/CarouselContext"
import { cn } from '@/utils/index'
import { CarouselProps } from "@/app/types/carousel"

export const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
  ({ orientation = "horizontal", opts, setApi, plugins, className, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel({ ...opts, axis: orientation === "horizontal" ? "x" : "y" }, plugins)

    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: { canScrollPrev: () => any; canScrollNext: () => any }) => {
      setCanScrollPrev(api?.canScrollPrev() ?? false)
      setCanScrollNext(api?.canScrollNext() ?? false)
    }, [])

    const scrollPrev = React.useCallback(() => api?.scrollPrev(), [api])
    const scrollNext = React.useCallback(() => api?.scrollNext(), [api])

    React.useEffect(() => {
      // If setApi is provided, update it with the api instance.
      setApi?.(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) return undefined // Explicitly return `undefined` if api is not available

      onSelect(api)

      api.on("select", onSelect)

      return () => {
        api.off("select", onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{ carouselRef, api, scrollPrev, scrollNext, canScrollPrev, canScrollNext, opts, orientation }}
      >
        <div ref={ref} className={cn("relative", className)} {...props}>
          {children}
        </div>
      </CarouselContext.Provider>
    )
  }
)

Carousel.displayName = "Carousel"
